import { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import {
  setOperationStatus,
  UI_OPERATION_STATUS_SUCCESS,
  UI_OPERATION_STATUS_ERROR,
  userRegisterWithPassword,
  userGetCurrent,
} from '@uncinc/react-drupal-core';

import useTypedDispatch from '../../../hooks/useTypedDispatch';

import {
  clearPageInformation,
  setPageInformation,
  setPageViewSent,
  getNewsletterContact,
  getNewsletterContactPropertyOptions,
  createOrUpdateNewsletterContactSubscription,
} from '../../../store/actions/UI';
import { createOrUpdateUserProfile } from '../../../store/actions/Profile';
import {
  ROUTE_ACCOUNT_EDIT,
  ROUTE_SUCCESS_REGISTER,
} from '../../../config/routes';
import {
  OTHER_FORM_ITEM_ID,
  SPOTLER_PERMISSIONS_BITS_WHITELIST,
  SPOTLER_PERMISSIONS_HIDDEN_DEFAULT_BITS,
  SPOTLER_SOURCE_REGISTRATION,
  GTM_SOURCE_REGULAR,
  GTM_SOURCE_WEBSHOP,
  GTM_SOURCE_LOGIN_WALL,
  GTM_SOURCE_RISK_MONITOR,
} from '../../../config/constants';
import { prepareSpotlerListData } from '../../../helpers';

import type { RegistrationFormValues, ProfileFormData } from './types';
import type {
  SpotlerPermissionOption,
  SpotlerSubscriptionValues,
  SpotlerListItem,
} from '../../../types/spotler';

interface UseRegistrationFlowParams {
  pageTitle: string;
  redirectUrl: string | null;
  entryNid: string | null;
  isLoginWallRegister: boolean;
  isSSORegister: boolean;
  totalPrice: number;
  analyticsItems: unknown[];
}

interface UseRegistrationFlowReturn {
  // State
  submittedFormValues: RegistrationFormValues | undefined;
  loggedIn: boolean;
  userRegisterStatus: string | null;
  profileUpdateStatus: string | null;
  subscriptionSaveStatus: string | null;

  // Actions
  handleSubmit: (
    values: RegistrationFormValues,
    form: { setSubmitting: (isSubmitting: boolean) => void }
  ) => void;
}

/**
 * Custom hook encapsulating the multi-step registration flow.
 * Preserves exact logic from original component.
 *
 * Flow:
 * 1. Initialize page and fetch contact property options
 * 2. If already logged in, redirect
 * 3. On form submit, register user with password
 * 4. After registration success, save profile data
 * 5. After profile saved, fetch Spotler contact
 * 6. After Spotler contact loaded, create/update subscription
 * 7. After subscription saved, redirect to success page
 */
const useRegistrationFlow = ({
  pageTitle,
  redirectUrl,
  entryNid,
  isLoginWallRegister,
  isSSORegister,
  totalPrice,
  analyticsItems,
}: UseRegistrationFlowParams): UseRegistrationFlowReturn => {
  const dispatch = useTypedDispatch();
  const history = useHistory();

  // Local state
  const [submittedFormValues, setSubmittedFormValues] = useState<
  RegistrationFormValues | undefined
  >();

  // Redux selectors
  const loggedIn = useSelector(
    (state: Immutable.Map<string, unknown>) => state.getIn(['login', 'loggedIn'], false) as boolean,
  );

  const uid = useSelector(
    (state: Immutable.Map<string, unknown>) => state.getIn(['login', 'uid']) as string | undefined,
  );

  // Check if profile data has been fetched into Redux (after userGetCurrent completes)
  const profileFirstName = useSelector(
    (state: Immutable.Map<string, unknown>) => {
      if (!uid) return undefined;
      return state.getIn(['user', uid, 'profile', 'field_first_name', 0, 'value']) as
        | string
        | undefined;
    },
  );

  const userRegisterStatus = useSelector(
    (state: Immutable.Map<string, unknown>) => state.getIn(
      ['ui', 'user', 'operations', 'register', 'status'],
    ) as string | null,
  );

  const profileUpdateStatus = useSelector(
    (state: Immutable.Map<string, unknown>) => state.getIn(
      ['ui', 'profile', 'operations', 'update', 'status'],
    ) as string | null,
  );

  const subscriptionSaveStatus = useSelector(
    (state: Immutable.Map<string, unknown>) => state.getIn(
      ['ui', 'newsletter', 'operations', 'update', 'status'],
    ) as string | null,
  );

  // Spotler subscription data
  const contactPermissionOptions = useSelector(
    (state: Immutable.Map<string, unknown>) => state.getIn(
      ['uiInternal', 'newsletter', 'contact_property_options', 'permissions'],
      null,
    ) as SpotlerPermissionOption[] | null,
  );

  const spotlerUserContact = useSelector(
    (state: Immutable.Map<string, unknown>) => state.getIn(
      ['uiInternal', 'newsletter', 'contact'],
      undefined,
    ) as Record<string, unknown> | null | undefined,
  );

  const spotlerWorkFieldList = useSelector(
    (state: Immutable.Map<string, unknown>) => state.getIn(
      ['uiInternal', 'newsletter', 'contact_property_options', 'list1'],
      undefined,
    ) as SpotlerListItem[] | undefined,
  );

  const spotlerJobTitleList = useSelector(
    (state: Immutable.Map<string, unknown>) => state.getIn(
      ['uiInternal', 'newsletter', 'contact_property_options', 'list2'],
      undefined,
    ) as SpotlerListItem[] | undefined,
  );

  // Effect 1: Initialize page on mount
  useEffect(() => {
    dispatch(userGetCurrent());
    dispatch(clearPageInformation());
    dispatch(setPageInformation(pageTitle));
    dispatch(setPageViewSent(false));
    dispatch(getNewsletterContactPropertyOptions());
  }, [dispatch, pageTitle]);

  // Effect 2: Redirect if already logged in (without form submission)
  useEffect(() => {
    if (loggedIn === true && !submittedFormValues) {
      if (redirectUrl) {
        window.location.replace(redirectUrl);
      } else {
        history.push(ROUTE_ACCOUNT_EDIT);
      }
    }
  }, [history, loggedIn, redirectUrl, submittedFormValues]);

  // Effect 3: After login success, save profile data
  useEffect(() => {
    if (loggedIn === true) {
      dispatch(setOperationStatus('user', 'register', null, null));

      if (submittedFormValues) {
        // Save profile information
        const profileFormData: ProfileFormData = Object.fromEntries(
          Object.entries(submittedFormValues).filter(
            ([key]) => key.startsWith('field_') && !key.endsWith('_other'),
          ),
        );

        if (profileFormData.field_work_area === OTHER_FORM_ITEM_ID) {
          profileFormData.field_work_area = submittedFormValues.field_work_area_other;
        }

        if (profileFormData.field_job_title === OTHER_FORM_ITEM_ID) {
          profileFormData.field_job_title = submittedFormValues.field_job_title_other;
        }

        // Pass the entry Node to the profile
        if (entryNid) {
          profileFormData.field_entry_node = [{ target_id: Number(entryNid) }];
        }

        // Address is required, add an empty one
        profileFormData.address = [{ country_code: 'NL' }];
        dispatch(createOrUpdateUserProfile(profileFormData));

        // Determine GTM source
        let sourceName = GTM_SOURCE_REGULAR;

        if (totalPrice > 0) {
          sourceName = GTM_SOURCE_WEBSHOP;
        }

        if (isLoginWallRegister) {
          sourceName = GTM_SOURCE_LOGIN_WALL;
        }

        if (isSSORegister) {
          sourceName = GTM_SOURCE_RISK_MONITOR;
        }

        // Push custom GTM event
        if (typeof window !== 'undefined') {
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({
            event: 'register_success',
            register_source: sourceName,
          });
        }
      }
    }
  }, [
    dispatch,
    entryNid,
    isLoginWallRegister,
    isSSORegister,
    loggedIn,
    submittedFormValues,
    totalPrice,
  ]);

  // Effect 4: After profile saved AND fetched into Redux, fetch Spotler contact
  // Note: We check profileFirstName to ensure userGetCurrent() has completed
  // and the profile data is actually available in Redux before proceeding
  useEffect(() => {
    if (
      loggedIn === true &&
      profileUpdateStatus === UI_OPERATION_STATUS_SUCCESS &&
      profileFirstName && // Wait for profile to be fetched into Redux
      submittedFormValues
    ) {
      dispatch(setOperationStatus('profile', 'update', null, null));
      // Try to fetch the Spotler contact with the given email address
      dispatch(getNewsletterContact(submittedFormValues.email));
    }

    // Handle profile update error - still redirect to success
    // (user is registered, profile update is not critical)
    if (
      loggedIn === true &&
      profileUpdateStatus === UI_OPERATION_STATUS_ERROR &&
      submittedFormValues
    ) {
      dispatch(setOperationStatus('profile', 'update', null, null));
      // Skip Spotler flow and redirect to success
      const targetUrl = redirectUrl || ROUTE_SUCCESS_REGISTER;
      if (
        redirectUrl &&
        redirectUrl !== ROUTE_ACCOUNT_EDIT &&
        redirectUrl !== ROUTE_SUCCESS_REGISTER
      ) {
        window.location.replace(redirectUrl);
      } else {
        history.push(`${targetUrl}${window.location.search}`);
      }
    }
  }, [
    dispatch,
    history,
    loggedIn,
    profileFirstName,
    profileUpdateStatus,
    redirectUrl,
    submittedFormValues,
  ]);

  // Effect 5: After Spotler contact loaded, create/update subscription
  useEffect(() => {
    // If the Spotler contact was loaded and we have submitted the form
    // TODO: https://uncinc.monday.com/boards/1674548856/pulses/8560148204
    if (spotlerUserContact !== undefined && submittedFormValues) {
      // Prepare Spotler data
      const spotlerListData = prepareSpotlerListData(
        {
          fields: spotlerWorkFieldList,
          selected:
            submittedFormValues.field_work_area === OTHER_FORM_ITEM_ID
              ? submittedFormValues.field_work_area_other
              : submittedFormValues.field_work_area,
          selected_other: submittedFormValues.field_work_area_other,
        },
        {
          fields: spotlerJobTitleList,
          selected:
            submittedFormValues.field_job_title === OTHER_FORM_ITEM_ID
              ? submittedFormValues.field_job_title_other
              : submittedFormValues.field_job_title,
          selected_other: submittedFormValues.field_job_title_other,
        },
      );

      // Assemble values
      const spotlerValues: SpotlerSubscriptionValues = {
        source: SPOTLER_SOURCE_REGISTRATION,
        email: submittedFormValues.email,
        firstName: submittedFormValues.field_first_name || '',
        lastName: submittedFormValues.field_infix_name
          ? `${submittedFormValues.field_infix_name} ${submittedFormValues.field_last_name}`
          : submittedFormValues.field_last_name || '',
        organisation: submittedFormValues.field_organisation_name || '',
        ...spotlerListData,
      };

      // Add all the permissions if we don't have a spotler account yet
      if (spotlerUserContact === null && contactPermissionOptions) {
        const newContactPermissions = contactPermissionOptions.map(
          (contactPropertyPermissionOption) => ({
            ...contactPropertyPermissionOption,
            enabled:
              SPOTLER_PERMISSIONS_BITS_WHITELIST.includes(
                contactPropertyPermissionOption.bit,
              ) ||
              SPOTLER_PERMISSIONS_HIDDEN_DEFAULT_BITS.includes(
                contactPropertyPermissionOption.bit,
              ),
          }),
        );
        spotlerValues.permissions = newContactPermissions;
      }

      // Create or update our Spotler newsletter subscription
      dispatch(createOrUpdateNewsletterContactSubscription(spotlerValues));
    }
  }, [
    contactPermissionOptions,
    dispatch,
    spotlerJobTitleList,
    spotlerUserContact,
    spotlerWorkFieldList,
    submittedFormValues,
  ]);

  // Effect 6: After subscription saved (or failed), redirect
  useEffect(() => {
    // Helper to perform redirect
    const performRedirect = () => {
      const targetUrl = redirectUrl || ROUTE_SUCCESS_REGISTER;
      if (
        redirectUrl &&
        redirectUrl !== ROUTE_ACCOUNT_EDIT &&
        redirectUrl !== ROUTE_SUCCESS_REGISTER
      ) {
        window.location.replace(redirectUrl);
      } else {
        history.push(`${targetUrl}${window.location.search}`);
      }
    };

    if (subscriptionSaveStatus === UI_OPERATION_STATUS_SUCCESS) {
      dispatch(setOperationStatus('newsletter', 'update', null, null));
      performRedirect();
    }

    // Handle newsletter subscription error - still redirect to success
    // (user is registered, newsletter subscription is not critical)
    if (subscriptionSaveStatus === UI_OPERATION_STATUS_ERROR) {
      dispatch(setOperationStatus('newsletter', 'update', null, null));
      performRedirect();
    }
  }, [dispatch, history, redirectUrl, subscriptionSaveStatus]);

  // Form submit handler
  const handleSubmit = useCallback(
    (
      values: RegistrationFormValues,
      form: { setSubmitting: (isSubmitting: boolean) => void },
    ) => {
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });

      if (typeof window !== 'undefined' && totalPrice > 0) {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'add_shipping_info',
          ecommerce: {
            currency: 'EUR',
            value: totalPrice,
            items: analyticsItems,
          },
        });
      }

      setSubmittedFormValues(values);
      // Register the user
      dispatch(
        userRegisterWithPassword(
          values.email,
          values.email,
          values.passwordRegister,
          undefined,
        ),
      );
      form.setSubmitting(false);
    },
    [analyticsItems, dispatch, totalPrice],
  );

  return {
    submittedFormValues,
    loggedIn,
    userRegisterStatus,
    profileUpdateStatus,
    subscriptionSaveStatus,
    handleSubmit,
  };
};

export default useRegistrationFlow;
